import { Injectable } from '@angular/core';
import { AllFeatures, StagedFeature, ToggleFeature, StagedStates, ToggleStates } from './types/accessibility.types';
import { isStagedFeature, isToggleFeature } from './utils/accessibility.utils';

@Injectable({ providedIn: 'root' })
export class BfsgAccessibilityService {
	private readonly STORAGE_KEY = 'bfsg-accessibility-settings';

	private stagedStates: StagedStates = {
		textSize: 0,
		lineHeight: 0,
		contrast: 0,
		saturation: 0,
	};

	private toggleStates: ToggleStates = {
		darkMode: false,
		highlightLinks: false,
		hideImages: false,
		pauseAnimation: false,
		dyslexicFont: false,
		bigCursor: false,
	};

	private featureStages: Record<StagedFeature, any[]> = {
		textSize: [1, 1.2, 1.4, 1.6],
		lineHeight: [1, 1.5, 2],
		contrast: ['none', 'bfsg-high-contrast', 'bfsg-inverted-contrast'],
		saturation: ['normal', 'bfsg-saturation-low', 'bfsg-saturation-high', 'bfsg-saturation-none'],
	};

	public applySavedSettings(): void {
		const settings = this.loadFeatureSettings();

		for (const [key, value] of Object.entries(settings)) {
			if (isStagedFeature(key)) {
				this.stagedStates[key] = value;
				this.applyFeatureStage(key);
			} else if (isToggleFeature(key)) {
				this.toggleStates[key] = value;
				this.toggleFeature(key, value);
			}
		}
		this.updateAccessibilityClass();
	}

	public cycleFeature(key: StagedFeature): void {
		const stages = this.featureStages[key];
		const current = this.stagedStates[key];
		const next = (current + 1) % stages.length;

		this.stagedStates[key] = next;
		this.applyFeatureStage(key);
		this.saveFeatureState(key, next);
		this.updateAccessibilityClass();
	}

	private updateAccessibilityClass(): void {
		const isAnyFeatureActive =
			Object.values(this.stagedStates).some(stage => stage > 0) || Object.values(this.toggleStates).some(flag => flag === true);

		document.documentElement.classList.toggle('bfsg-accessibility', isAnyFeatureActive);
	}

	public toggleFeature(feature: ToggleFeature, forceState?: boolean): void {
		const enable = typeof forceState === 'boolean' ? forceState : !this.toggleStates[feature];
		this.toggleStates[feature] = enable;

		switch (feature) {
			case 'highlightLinks':
				this.highlightLinks(enable);
				break;
			case 'darkMode':
				this.toggleDarkMode(enable);
				break;
			case 'hideImages':
				this.hideImages(enable);
				break;
			case 'pauseAnimation':
				this.pauseAnimations(enable);
				break;
			case 'dyslexicFont':
				this.applyDyslexicFont(enable);
				break;
			case 'bigCursor':
				this.toggleBigCursor(enable);
				break;
		}

		this.saveFeatureState(feature, enable);
		this.updateAccessibilityClass();
	}

	public resetAllStyles(): void {
		Object.keys(this.stagedStates).forEach(key => {
			const stage = key as StagedFeature;
			this.stagedStates[stage] = 0;
			this.applyFeatureStage(stage);
			this.saveFeatureState(stage, 0);
		});

		Object.keys(this.toggleStates).forEach(key => {
			const toggle = key as ToggleFeature;
			this.toggleStates[toggle] = false;
			this.toggleFeature(toggle, false);
			this.saveFeatureState(toggle, false);
		});

		localStorage.removeItem(this.STORAGE_KEY);
		this.updateAccessibilityClass();
	}

	public getFeatureLabel(key: StagedFeature): string {
		const stage = this.stagedStates[key];
		switch (key) {
			case 'textSize':
				return ['Normal', 'Large', 'XL', 'XXL'][stage];
			case 'lineHeight':
				return ['Default', 'Relaxed', 'Spacious'][stage];
			case 'contrast':
				return ['None', 'High', 'Inverted'][stage];
			case 'saturation':
				return ['Normal', 'Low', 'High', 'Desaturate'][stage];
		}
	}

	private applyFeatureStage(key: StagedFeature): void {
		const stage = this.stagedStates[key];
		const value = this.featureStages[key][stage];

		switch (key) {
			case 'textSize':
				this.resetFontSize();
				if (value !== 1) this.adjustFontSize(value);
				break;
			case 'lineHeight':
				this.adjustLineHeight(value);
				break;
			case 'contrast':
			case 'saturation':
				document.documentElement.classList.remove(...this.featureStages[key]);
				if (value !== 'none' && value !== 'normal') {
					document.documentElement.classList.add(value);
				}
				break;
		}
	}

	private adjustLineHeight(factor: number): void {
		document.body.style.lineHeight = `${factor}`;

		const textTags = [
			'p',
			'li',
			'label',
			'a',
			'td',
			'th',
			'h1',
			'h2',
			'h3',
			'h4',
			'h5',
			'h6',
			'ion-label',
			'ion-title',
			'ion-text',
			'span',
			'ion-button',
			'ion-input',
			'button',
			'ion-col',
		];
		const selector = textTags.map(tag => `${tag}:not(.bfsg-widget, .bfsg-widget *)`).join(',');
		document.querySelectorAll<HTMLElement>(selector).forEach(el => {
			if (el.hasAttribute('data-bfsg-ignore')) return;
			el.style.lineHeight = `${factor}`;
		});
	}

	private saveFeatureState(key: AllFeatures, value: any): void {
		const settings = this.loadFeatureSettings();
		settings[key] = value;
		localStorage.setItem(this.STORAGE_KEY, JSON.stringify(settings));
	}

	private loadFeatureSettings(): Record<string, any> {
		const raw = localStorage.getItem(this.STORAGE_KEY);
		return raw ? JSON.parse(raw) : {};
	}

	private highlightLinks(enable: boolean): void {
		document.querySelectorAll('a').forEach(link => {
			const el = link as HTMLElement;
			el.style.setProperty('background-color', enable ? 'yellow' : '');
			el.style.setProperty('text-decoration', enable ? 'underline' : '');
		});
	}

	private toggleDarkMode(enable: boolean): void {
		document.documentElement.classList.toggle('bfsg-dark-mode', enable);
		setTimeout(() => this.applySmartTextColor(), 100);
	}

	private hideImages(enable: boolean): void {
		const display = enable ? 'none' : '';

		// Hide/show <img>, <picture>, [role="img"], ion-img
		document.querySelectorAll('img, picture, [role="img"], ion-img').forEach(el => {
			(el as HTMLElement).style.display = display;
		});

		// Hide/show background images
		document.querySelectorAll<HTMLElement>('*').forEach(el => {
			const computedStyle = window.getComputedStyle(el);
			const currentBg = computedStyle.backgroundImage;

			if (enable) {
				if (currentBg && currentBg !== 'none') {
					// Save computed background
					el.setAttribute('data-bfsg-bg', currentBg);
					// Save inline background (if any)
					if (el.style.backgroundImage && el.style.backgroundImage !== 'none') {
						el.setAttribute('data-bfsg-bg-inline', el.style.backgroundImage);
					}
					el.style.setProperty('background-image', 'none', 'important');
				}
			} else {
				if (el.hasAttribute('data-bfsg-bg')) {
					const inlineOriginal = el.getAttribute('data-bfsg-bg-inline');

					if (inlineOriginal) {
						el.style.setProperty('background-image', inlineOriginal);
					} else {
						// Remove inline override so CSS can take over
						el.style.removeProperty('background-image');
					}

					el.removeAttribute('data-bfsg-bg');
					el.removeAttribute('data-bfsg-bg-inline');
				}
			}
		});
	}

	private pauseAnimations(enable: boolean): void {
		document.body.style.setProperty('animation', enable ? 'none !important' : '');
		document.body.style.setProperty('transition', enable ? 'none !important' : '');
	}

	private applyDyslexicFont(enable: boolean): void {
		document.documentElement.classList.toggle('bfsg-dyslexic-font', enable);
	}

	private toggleBigCursor(enable: boolean): void {
		const id = 'bfsg-big-cursor-style';
		const existing = document.getElementById(id);
		if (enable && !existing) {
			const style = document.createElement('style');
			style.id = id;
			style.innerHTML = `* { cursor: url('https://cdn.userway.org/widgetapp/images/arrow_w.svg') 16 16, auto; }`;
			document.head.appendChild(style);
		} else if (!enable && existing) {
			existing.remove();
		}
	}

	private adjustFontSize(factor: number): void {
		const textTags = [
			'p',
			'li',
			'label',
			'a',
			'td',
			'th',
			'h1',
			'h2',
			'h3',
			'h4',
			'h5',
			'h6',
			'ion-label',
			'ion-title',
			'ion-text',
			'span',
			'ion-button',
			'ion-input',
			'button',
			'ion-col',
		];
		const selector = textTags.map(tag => `${tag}:not(.bfsg-widget, .bfsg-widget *)`).join(',');
		document.querySelectorAll<HTMLElement>(selector).forEach(el => {
			if (el.hasAttribute('data-bfsg-ignore')) return;
			const computed = window.getComputedStyle(el);
			const originalFontSize = el.getAttribute('data-original-font-size') || computed.fontSize;
			const originalLineHeight = el.getAttribute('data-original-line-height') || computed.lineHeight;
			const baseSize = parseFloat(originalFontSize);
			if (isNaN(baseSize)) return;

			if (!el.hasAttribute('data-original-font-size')) el.setAttribute('data-original-font-size', originalFontSize);
			if (!el.hasAttribute('data-original-line-height')) el.setAttribute('data-original-line-height', originalLineHeight);

			const newSize = baseSize * factor;
			el.style.fontSize = `${newSize}px`;
			el.style.lineHeight = `${(newSize * 1.35).toFixed(2)}px`;
		});
	}

	private resetFontSize(): void {
		document.querySelectorAll<HTMLElement>('[data-original-font-size], [data-original-line-height]').forEach(el => {
			const fontSize = el.getAttribute('data-original-font-size');
			const lineHeight = el.getAttribute('data-original-line-height');
			if (fontSize) el.style.fontSize = fontSize;
			if (lineHeight) el.style.lineHeight = lineHeight;
			el.removeAttribute('data-original-font-size');
			el.removeAttribute('data-original-line-height');
		});
	}

	private applySmartTextColor(selector = 'ion-col'): void {
		document.querySelectorAll<HTMLElement>(selector).forEach(el => {
			if (!el.offsetParent) return;
			const bg = this.getComputedBackgroundColor(el);
			const brightness = this.getBrightness(bg);
			el.style.color = brightness > 150 ? '#000000' : '#ffffff';
		});
	}

	private getComputedBackgroundColor(el: HTMLElement): string {
		let bg = window.getComputedStyle(el).backgroundColor;
		if (bg === 'rgba(0, 0, 0, 0)' || bg === 'transparent') {
			return el.parentElement ? this.getComputedBackgroundColor(el.parentElement) : 'rgb(255,255,255)';
		}
		return bg;
	}

	private getBrightness(rgb: string): number {
		const match = rgb.match(/\d+/g);
		if (!match) return 255;
		const [r, g, b] = match.map(Number);
		return 0.299 * r + 0.587 * g + 0.114 * b;
	}
}
